import noPhotoSmall from '@propertypal/shared/src/resources/fallbacks/no-photo-icon-small.png';
import noPhoto from '@propertypal/shared/src/resources/fallbacks/no-photo-icon.png';
import { Property } from '@propertypal/shared/src/types/property';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import { getMainImage, getThumbImages, getAgentLogos } from '@propertypal/shared/src/utils/property';
import { SubHeading, Text } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import router from 'next/router';
import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import { Box } from '../layout';
import StartEndTime from '../time/StartEndTime';
import EnergyRatingIcon from './EnergyRatingIcon';
import OpenViewingOverlay from './OpenViewingOverlay';
import PropertyBadge from './PropertyBadge';
import PropertyBanner from './PropertyBanner';
import {
  Container,
  Content,
  DescriptionBox,
  FavouriteBox,
  HeroImage,
  HeroImageBox,
  HeroNoImage,
  ImageContent,
  LinkContainer,
  ThumbImage,
  ThumbImageContainer,
  Wrapper,
} from './PropertyBox.style';
import PropertyBoxFooter from './PropertyBoxFooter';
import PropertyDistance from './PropertyDistance';
import PropertyFavourite from './PropertyFavourite';
import PropertyPrice from './PropertyPrice';

interface Props {
  property: Property;
  disablePremiumProperties?: boolean;
  userSection?: boolean;
  onFavourite?: () => (favourited: boolean, propertyId: number) => Promise<boolean>;
  overlay?: React.ReactElement;
  hideFooter?: boolean;
  locationDetail?: PropertySearchResult['locationDetail'];
  imageLocator?: string;
  thumbImageLocator?: string;
  isAuction?: boolean;
  isOpenViewing?: boolean;
  isSingleResult?: boolean;
}

const PropertyBox: FunctionComponent<Props> = (props) => {
  const { property, overlay, hideFooter } = props;
  const isPremium =
    !props.disablePremiumProperties && props.property.premiumListing && !props.userSection && !props.isOpenViewing;
  const theme = useTheme();
  const heroImage = getMainImage(property, props.imageLocator);
  const thumbImages = getThumbImages(property, props.thumbImageLocator);
  const accountLogos = getAgentLogos(property, 'standard', props?.userSection);
  const showAuctionTime = !props.userSection && !isPremium && props.isAuction && property.auction;
  const showOpenViewingTime = !props.userSection && !isPremium && props.isOpenViewing && property.openViewing;
  const renderOpenViewingOverlay = !showOpenViewingTime && property.openViewing && !property.openViewing.past;

  const renderHeroImg = () => {
    if (!heroImage) {
      return <HeroNoImage src={props?.userSection ? noPhotoSmall.src : noPhoto.src} alt="No photo available" />;
    }

    return (
      <HeroImage
        src={heroImage}
        alt={`${property.addressLine1} photo`}
        aria-hidden="true"
        data-testid="propbox-img"
        width={880}
        height={645}
        $userSection={props?.userSection}
      />
    );
  };

  return (
    <Container
      className="pp-property-box"
      premium={isPremium}
      $userSection={!!props?.userSection}
      isSingleResult={props.isSingleResult}
    >
      {(showAuctionTime || showOpenViewingTime) && (
        <StartEndTime
          startTime={showAuctionTime ? property.auctionTime : property.openViewing?.startTime}
          endTime={showAuctionTime ? undefined : property.openViewing?.endTime}
          propBoxAuction={showAuctionTime && !showOpenViewingTime}
          cancelled={showOpenViewingTime && property.openViewing?.cancelled}
        />
      )}

      {!!props.property.distance && (
        <PropertyDistance distance={props.property.distance} locationDetail={props.locationDetail} />
      )}

      <Wrapper
        $userSection={!!props?.userSection}
        premium={isPremium}
        premierColor={props.property.account.logo?.premierBackgroundColour}
      >
        {overlay}
        <Link href={property.path} passHref>
          <LinkContainer premium={isPremium} $userSection={props?.userSection} isSingleResult={props.isSingleResult}>
            <Content premium={isPremium} $userSection={props?.userSection} isSingleResult={props.isSingleResult}>
              <ImageContent premium={isPremium} $userSection={props?.userSection} isSingleResult={props.isSingleResult}>
                <HeroImageBox premium={isPremium} $userSection={props?.userSection}>
                  {renderHeroImg()}

                  {props.onFavourite && property.type === 'property' && (
                    <FavouriteBox>
                      <PropertyFavourite
                        favourited={property.shortlisted}
                        // @ts-ignore
                        onFavourite={(favourited) => props.onFavourite()(favourited, property.id)}
                        // unique key needed in infinite grid, once item above is removed from list
                        // it would inherit its favourited state value in PropertyFavourite
                        key={property.id}
                      />
                    </FavouriteBox>
                  )}

                  {!props.disablePremiumProperties && <PropertyBanner property={property} />}

                  {/* Prevent auction badge from rendering when there is also an open viewing on property */}
                  {(!showAuctionTime || (showAuctionTime && !property.auctionTime)) && !renderOpenViewingOverlay && (
                    <PropertyBadge property={property} />
                  )}

                  {renderOpenViewingOverlay && property.openViewing && (
                    <OpenViewingOverlay openViewing={property.openViewing} />
                  )}
                </HeroImageBox>

                {!props.disablePremiumProperties && (
                  <ThumbImageContainer premium={isPremium}>
                    {thumbImages &&
                      thumbImages.map((thumbImage, index) => (
                        <ThumbImage
                          key={thumbImage}
                          src={thumbImage}
                          alt={`${property.addressLine1} photo ${index + 2}`}
                          aria-hidden="true"
                          premium={isPremium}
                        />
                      ))}
                  </ThumbImageContainer>
                )}
              </ImageContent>

              <DescriptionBox $userSection={props?.userSection}>
                <div>
                  <SubHeading fontSize={20} mb={12} fontWeight="600">
                    {property.displayAddress}
                  </SubHeading>

                  <PropertyPrice
                    containerStyle={{ marginBottom: 20 }}
                    price={property.price}
                    alternativeCurrencyPrice={property.alternativeCurrencyPrice}
                    fontSize={14}
                    status={property.status}
                    comingSoonText={property.comingSoonText}
                  />

                  <Text fontSize={14} color={theme.textMid} mb={10}>
                    {props.property.briefText}
                  </Text>

                  {!!property.showHomeOpeningTimes && (
                    <>
                      <SubHeading mt={13} fontSize={14}>
                        Show Home Open
                      </SubHeading>

                      <Text color={theme.textMid} data-testid="openingTimesText">
                        {property.showHomeOpeningTimes}
                      </Text>
                    </>
                  )}

                  <EnergyRatingIcon property={property} hideEpcText />

                  {property.development && !hideFooter && (
                    <Button
                      containerStyle={{ marginTop: 10 }}
                      onClick={(e) => {
                        e.preventDefault();
                        router.push(property.development?.path || '');
                      }}
                    >
                      View Development
                    </Button>
                  )}

                  {isPremium && (
                    <Box maxWidth={600}>
                      <SubHeading mt={30} fontSize={16}>
                        DESCRIPTION
                      </SubHeading>

                      <Text data-testid="propertyDescription" fontSize={14}>
                        {property.description}
                      </Text>
                    </Box>
                  )}
                </div>

                {!isPremium && !hideFooter && (
                  <PropertyBoxFooter key="1" property={property} premium={isPremium} logos={accountLogos} />
                )}
              </DescriptionBox>
            </Content>

            {isPremium && !hideFooter && (
              <PropertyBoxFooter key="2" property={property} premium={isPremium} logos={accountLogos} />
            )}
          </LinkContainer>
        </Link>
      </Wrapper>
    </Container>
  );
};

export default React.memo(PropertyBox);
